import React, { useMemo, useState } from 'react';
import { Platform, StyleSheet, Text, View, ScrollView, TouchableOpacity } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';
import { PRIMARY_BLUE, WARNING_RED } from '../../../constants/colors';
import { CrewingVerticalScrollView } from './webScrollStyles';
import { useTheme } from '../../../../../theme/useTheme';

export interface HeatTableRow {
  label: string;
  accuracy: number;
  efficiency: number;
  compliance: number;
  kcphIndex: number;
}

type HeatSortKey = keyof HeatTableRow;

interface HeatTableCardProps {
  title: string;
  subtitle?: string;
  description?: string;
  rows: HeatTableRow[];
  /** Prior-period values (same shape/labels) used to render a small "vs prior" delta per cell. */
  priorRows?: HeatTableRow[];
  rowLabelHeader?: string;
}

// Heat cell backgrounds are applied inline (not via StyleSheet), so the global dark-mode
// stylesheet sweep never reaches them — they must be resolved per theme here.
const HEAT_ABOVE_LIGHT = '#F3F7FD';
const HEAT_BELOW_LIGHT = '#FFE5DE';
const HEAT_ABOVE_DARK = '#1B3A66';
const HEAT_BELOW_DARK = '#7A2A1A';

const getHeatColor = (value: number, target: number = 95, isDark: boolean = false): string => {
  if (isDark) return value >= target ? HEAT_ABOVE_DARK : HEAT_BELOW_DARK;
  return value >= target ? HEAT_ABOVE_LIGHT : HEAT_BELOW_LIGHT;
};

const getHeatTextColor = (value: number, target: number = 95, isDark: boolean = false): string => {
  if (isDark) return '#FFFFFF';
  return value >= target ? PRIMARY_BLUE : WARNING_RED;
};

const renderDelta = (delta: number | undefined, isDark: boolean = false) => {
  if (delta == null || isNaN(delta) || Math.abs(delta) < 0.05) return null;
  const isUp = delta > 0;
  return (
    <Text style={[styles.deltaText, isUp ? styles.deltaUp : styles.deltaDown, isDark && styles.deltaDark]}>
      {`${isUp ? '▲' : '▼'}${Math.abs(delta).toFixed(1)} vs prior`}
    </Text>
  );
};

const HeatTableCard: React.FC<HeatTableCardProps> = ({
  title,
  subtitle,
  description,
  rows,
  priorRows = [],
  rowLabelHeader = 'Region',
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [sortKey, setSortKey] = useState<HeatSortKey>('label');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const priorByLabel = useMemo(() => {
    const map = new Map<string, HeatTableRow>();
    priorRows.forEach((row) => map.set(row.label, row));
    return map;
  }, [priorRows]);

  const handleSort = (key: HeatSortKey) => {
    if (key === sortKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortedRows = useMemo(() => {
    const compared = [...rows].sort((a, b) => {
      if (sortKey === 'label') return a.label.localeCompare(b.label);
      return a[sortKey] - b[sortKey];
    });
    return sortDirection === 'asc' ? compared : compared.reverse();
  }, [rows, sortKey, sortDirection]);

  const renderSortHeader = (key: HeatSortKey, label: string, extraStyle?: object) => {
    const isActive = sortKey === key;
    return (
      <TouchableOpacity
        style={[styles.cell, styles.headerCell, extraStyle, styles.headerCellSortable]}
        onPress={() => handleSort(key)}
        activeOpacity={0.7}
      >
        <Text style={[styles.headerText, isActive && styles.headerTextActive]}>{label}</Text>
        <MaterialIcons
          name={isActive ? (sortDirection === 'asc' ? 'arrow-drop-up' : 'arrow-drop-down') : 'unfold-more'}
          size={14}
          color={isActive ? '#034895' : '#9CA3AF'}
        />
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.card}>
      <View style={[styles.headerRow, Platform.OS === 'web' && styles.headerRowWeb]}>
        <View style={[styles.titleRow, Platform.OS === 'web' && styles.titleRowWeb]}>
          <Text style={styles.title}>{title}</Text>
          {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
        </View>
        {description && <Text style={[styles.description, Platform.OS === 'web' && styles.descriptionWeb]}>{description}</Text>}
      </View>

      {/* Legend */}
      <View style={styles.legend}>
        <View style={styles.legendItem}>
          <View
            style={[styles.legendSwatch, { backgroundColor: isDark ? HEAT_ABOVE_DARK : HEAT_ABOVE_LIGHT }]}
          />
          <Text style={styles.legendText}>At / Above Target</Text>
        </View>
        <View style={styles.legendItem}>
          <View
            style={[styles.legendSwatch, { backgroundColor: isDark ? HEAT_BELOW_DARK : HEAT_BELOW_LIGHT }]}
          />
          <Text style={styles.legendText}>Below Target</Text>
        </View>
      </View>

      <View style={styles.tableWrapper}>
        {/* Header Row */}
        <View style={styles.row}>
          {renderSortHeader('label', rowLabelHeader, styles.labelCell)}
          {renderSortHeader('accuracy', 'Accuracy')}
          {renderSortHeader('efficiency', 'Efficiency')}
          {renderSortHeader('compliance', 'Compliance')}
          {renderSortHeader('kcphIndex', 'KCPH Index')}
        </View>

        <View style={styles.bodyViewport}>
          <CrewingVerticalScrollView nestedScrollEnabled showsVerticalScrollIndicator>
            {/* Data Rows */}
            {sortedRows.map((row) => {
              const prior = priorByLabel.get(row.label);
              return (
              <View key={row.label} style={styles.row}>
                <View style={[styles.cell, styles.labelCell]}>
                  <Text style={styles.cellText}>{row.label}</Text>
                </View>
                <View
                  style={[
                    styles.cell,
                    { backgroundColor: getHeatColor(row.accuracy, 95, isDark) },
                  ]}
                >
                  <Text style={[styles.cellValue, { color: getHeatTextColor(row.accuracy, 95, isDark) }]}>{row.accuracy.toFixed(1)}%</Text>
                  {renderDelta(prior ? row.accuracy - prior.accuracy : undefined, isDark)}
                </View>
                <View
                  style={[
                    styles.cell,
                    { backgroundColor: getHeatColor(row.efficiency, 95, isDark) },
                  ]}
                >
                  <Text style={[styles.cellValue, { color: getHeatTextColor(row.efficiency, 95, isDark) }]}>{row.efficiency.toFixed(1)}%</Text>
                  {renderDelta(prior ? row.efficiency - prior.efficiency : undefined, isDark)}
                </View>
                <View
                  style={[
                    styles.cell,
                    { backgroundColor: getHeatColor(row.compliance, 95, isDark) },
                  ]}
                >
                  <Text style={[styles.cellValue, { color: getHeatTextColor(row.compliance, 95, isDark) }]}>{row.compliance.toFixed(1)}%</Text>
                  {renderDelta(prior ? row.compliance - prior.compliance : undefined, isDark)}
                </View>
                <View
                  style={[
                    styles.cell,
                    { backgroundColor: getHeatColor(row.kcphIndex, 100, isDark) },
                  ]}
                >
                  <Text style={[styles.cellValue, { color: getHeatTextColor(row.kcphIndex, 100, isDark) }]}>{row.kcphIndex.toFixed(1)}%</Text>
                  {renderDelta(prior ? row.kcphIndex - prior.kcphIndex : undefined, isDark)}
                </View>
              </View>
              );
            })}
          </CrewingVerticalScrollView>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    flex: 1,
    minHeight: 360,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 1,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 14,
    flexWrap: 'wrap',
    width: '100%',
  },
  titleRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 4,
    width: '100%',
  },
  title: {
    fontSize: 16,
    lineHeight: 20,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#838383',
  },
  description: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#838383',
    width: '100%',
    marginTop: 4,
  },
  headerRowWeb: {
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  titleRowWeb: {
    flex: 1,
    width: 'auto',
    flexWrap: 'nowrap',
    columnGap: 4,
  },
  descriptionWeb: {
    width: 'auto',
    marginTop: 0,
    marginLeft: 12,
    flexShrink: 1,
    textAlign: 'right',
  },
  tableWrapper: {
    borderWidth: 1,
    borderColor: '#E3E9F3',
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 12,
    flex: 1,
  },
  bodyViewport: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E3E9F3',
  },
  cell: {
    flex: 1,
    paddingVertical: 11,
    paddingHorizontal: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerCell: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
  },
  headerCellSortable: {
    flexDirection: 'row',
    gap: 2,
  },
  labelCell: {
    flex: 1.2,
    alignItems: 'flex-start',
  },
  headerText: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#616161',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  headerTextActive: {
    color: '#034895',
  },
  cellText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    fontWeight: '500',
    color: '#111827',
  },
  cellValue: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    fontWeight: '600',
  },
  deltaText: {
    fontSize: 9,
    fontFamily: FontFamily.medium,
    color: '#FFFFFF',
    marginTop: 1,
  },
  deltaUp: {
    color: '#0B5FC6',
    opacity: 0.95,
  },
  deltaDown: {
    color: '#C12C01',
    opacity: 0.95,
  },
  deltaDark: {
    color: '#FFFFFF',
    opacity: 0.85,
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E3E9F3',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 2,
  },
  legendText: {
    fontSize: 11,
    color: '#6B7280',
  },
});

export default HeatTableCard;
